import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { todayStr, fmtDate, OPERATION_TYPES } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";
import MapView from "../components/MapView";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Sparkles, Truck, User, Clock, MapPin, ArrowUp, ArrowDown, Lock, Unlock,
  Send, Trash2, Printer, AlertTriangle, Route as RouteIcon, Gauge, RefreshCw, CalendarClock, Camera,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function tourBlockHtml(t) {
  const rows = (t.stops || []).map((s, i) => {
    const appt = s.appointment
      ? `<div style="color:${s.appointment_late ? "#991B1B" : "#5B3E86"};font-weight:600">Appuntamento ${s.appointment}${s.appointment_late ? ` (ritardo +${s.appointment_late_min}′)` : ""}</div>`
      : "";
    const maps = s.lat != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || "")}`;
    const ops = (s.operations || []).map((o) =>
      `<div>${o.code} · ${OPERATION_TYPES[o.operation_type]} · ${o.goods_description || ""}${o.needs?.length ? ` · <i>${o.needs.join(", ")}</i>` : ""} <span style="color:#8a7a6a">(rich. ${o.requester_name || "—"})</span>${o.notes ? `<br/><span style="color:#9A6B00">Nota: ${o.notes}</span>` : ""}</div>`).join("");
    return `<tr><td style="text-align:center;font-weight:700">${i + 1}</td>
      <td><b>${s.label || ""}</b><br/><a href="${maps}" style="color:#8C4A32">${s.address || ""}</a></td>
      <td>Arrivo ${s.eta_arrive || "—"}<br/>Ripart. ${s.eta_depart || "—"}${appt}</td>
      <td>${ops}</td></tr>`;
  }).join("");
  return `<div class="tour"><div class="thead">
      <h2>Giro ${t.code}</h2>
      <p class="meta">${fmtDate(t.date)} · Mezzo ${t.vehicle?.identifier || "—"} · Autista ${t.driver?.name || "—"} · ${t.total_km || 0} km · ${Math.round((t.total_duration_min || 0) / 60 * 10) / 10} h</p></div>
      <table><thead><tr><th>#</th><th>Tappa</th><th>Orari</th><th>Operazioni</th></tr></thead><tbody>${rows}</tbody></table>
      ${t.ai_summary ? `<div class="note"><b>Nota di viaggio:</b>\n${t.ai_summary}</div>` : ""}</div>`;
}

function openPrintWindow(title, inner) {
  const w = window.open("", "_blank");
  const logo = `${window.location.origin}/logo-mascheroni.webp`;
  w.document.write(`<html><head><title>${title}</title><style>
    @page{margin:14mm}
    body{font-family:'Helvetica Neue',Arial,sans-serif;color:#2A1E17;margin:0}
    .head{display:flex;align-items:center;gap:16px;border-bottom:3px solid #8C4A32;padding:0 0 14px;margin-bottom:16px}
    .head img{height:44px}.head h1{font-size:22px;margin:0;color:#5A3B28}
    .tour{margin-bottom:26px;page-break-inside:avoid}.pagebreak{page-break-after:always}
    .thead h2{font-size:17px;margin:0 0 2px;color:#5A3B28}.meta{font-size:12px;color:#5A493C;margin:0 0 6px}
    table{width:100%;border-collapse:collapse;margin-top:4px}
    td,th{border:1px solid #D5C5B5;padding:8px;text-align:left;font-size:12px;vertical-align:top}
    th{background:#F4ECE1;color:#5A3B28;text-transform:uppercase;font-size:11px;letter-spacing:.05em}
    .note{margin-top:10px;padding:9px 11px;background:#F4ECE1;border-radius:8px;font-size:12px;white-space:pre-line}
    .foot{margin-top:20px;font-size:11px;color:#9A8B7A;text-align:center}
  </style></head><body>
    <div class="head"><img src="${logo}" alt="Ludovica Mascheroni"/><div><h1>${title}</h1></div></div>
    ${inner}
    <p class="foot">Ludovica Mascheroni · Rimalcuoio — documento generato automaticamente</p>
  </body></html>`);
  w.document.close(); w.focus();
  const img = w.document.querySelector(".head img");
  const go = () => setTimeout(() => w.print(), 250);
  if (img && !img.complete) { img.onload = go; img.onerror = go; } else { go(); }
}

export default function Planner() {
  const [date, setDate] = useState(todayStr());
  const [tours, setTours] = useState([]);
  const [unplannable, setUnplannable] = useState([]);
  const [pending, setPending] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [distSource, setDistSource] = useState(null);

  const load = useCallback(async () => {
    const [t, p, v, d] = await Promise.all([
      api.get(`/tours?date=${date}`),
      api.get(`/requests?status=da_pianificare`),
      api.get(`/vehicles`),
      api.get(`/users?role=autista`),
    ]);
    setTours(t.data); setPending(p.data); setVehicles(v.data); setDrivers(d.data);
    if (t.data[0]) setDistSource(t.data[0].distance_source);
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/planning/generate", { date });
      setUnplannable(data.unplannable || []);
      setDistSource(data.distance_source);
      const ap = data.auto_published || [];
      toast.success(`Proposta generata: ${data.tours.length} giri, ${data.unplannable.length} non pianificabili`
        + (ap.length ? ` · ${ap.length} pubblicati automaticamente` : ""));
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setGenLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-lm-text">Pianificazione Giri</h1>
          <p className="text-sm text-lm-muted">Proposta automatica, modifica manuale e pubblicazione</p>
        </div>
        <div className="flex items-end gap-2">
          <div><label className="text-xs text-lm-text2 block mb-1">Data</label>
            <Input data-testid="planner-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white w-44" /></div>
          <Button data-testid="generate-btn" onClick={generate} disabled={genLoading} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream">
            {genLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Genera proposta
          </Button>
          {tours.length > 0 && (
            <Button variant="outline" data-testid="print-all-btn"
              onClick={() => openPrintWindow(`Foglio viaggio del ${fmtDate(date)}`, tours.map((tt, i) => tourBlockHtml(tt) + (i < tours.length - 1 ? '<div class="pagebreak"></div>' : "")).join(""))}
              className="border-lm-sand text-lm-brown"><Printer className="w-4 h-4 mr-2" /> Stampa tutti i giri</Button>
          )}
        </div>
      </div>

      {distSource && (
        <div className={`text-xs rounded-lg px-3 py-2 border inline-flex items-center gap-2 ${distSource === "strade_reali" ? "bg-[#EFF7F2] border-[#9AD4AC] text-[#166534]" : "bg-[#FEF8EC] border-[#F3D295] text-[#9A6B00]"}`}>
          <Gauge className="w-4 h-4" />
          {distSource === "strade_reali" ? "Distanze/tempi su percorsi stradali reali (OpenRouteService)." : "Distanze stimate in linea d'aria — non percorsi reali."}
        </div>
      )}

      {unplannable.length > 0 && (
        <div className="lm-card p-4 border-[#F8B4B4]" data-testid="unplannable-panel">
          <p className="font-semibold text-[#991B1B] flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4" /> Richieste non pianificabili ({unplannable.length})</p>
          <ul className="space-y-1 text-sm">
            {unplannable.map((u) => <li key={u.request_id} className="text-lm-text2"><b className="font-mono text-[#991B1B]">{u.code}</b> — {u.reason}</li>)}
          </ul>
        </div>
      )}

      {pending.length > 0 && (
        <div className="lm-card p-4">
          <p className="font-semibold text-lm-brown mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> In attesa di pianificazione ({pending.length})</p>
          <div className="flex flex-wrap gap-2">
            {pending.map((r) => <span key={r.id} className="text-xs px-2.5 py-1 rounded-lg bg-lm-sandsoft text-lm-brown">{r.code} · {OPERATION_TYPES[r.operation_type]} · {r.supplier_name || r.origin_address || "—"}</span>)}
          </div>
        </div>
      )}

      {tours.length === 0 ? (
        <div className="lm-card p-10 text-center text-lm-muted">
          <RouteIcon className="w-10 h-10 mx-auto mb-3 text-lm-sand" />
          Nessun giro per il {fmtDate(date)}. Genera una proposta automatica oppure aggiungi richieste con coordinate GPS.
        </div>
      ) : (
        <div className="space-y-4">
          {tours.map((t) => <TourCard key={t.id} tour={t} vehicles={vehicles} drivers={drivers} reload={load} />)}
        </div>
      )}
    </div>
  );
}

function TourCard({ tour, vehicles, drivers, reload }) {
  const [t, setT] = useState(tour);
  const [aiLoading, setAiLoading] = useState(false);
  useEffect(() => { setT(tour); }, [tour]);

  const genAI = async () => {
    setAiLoading(true);
    try { const { data } = await api.post(`/tours/${t.id}/ai-summary`); setT((x) => ({ ...x, ai_summary: data.ai_summary })); toast.success("Nota di viaggio generata"); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setAiLoading(false); }
  };

  const stopsToPayload = (stops) => stops.map((s, i) => ({
    id: s.id, label: s.label, address: s.address, lat: s.lat, lng: s.lng,
    order: i, locked: !!s.locked, request_ids: s.request_ids || (s.operations || []).map((o) => o.id),
  }));

  const persist = async (stops, extra = {}) => {
    const body = { date: t.date, vehicle_id: extra.vehicle_id ?? t.vehicle_id, driver_id: extra.driver_id ?? t.driver_id, stops: stopsToPayload(stops), notes: t.notes };
    try { const { data } = await api.put(`/tours/${t.id}`, body); setT(data); reload(); } catch (e) { toast.error(apiErr(e)); }
  };

  const proposedEarly = () => {
    const late = (t.stops || []).filter((s) => s.appointment_late);
    if (!late.length) return null;
    const drv = drivers.find((d) => d.id === t.driver_id);
    const ws = t.start_time || drv?.work_start || "08:00";
    const [h, m] = ws.split(":").map(Number);
    const maxLate = Math.max(...late.map((s) => s.appointment_late_min || 0));
    const mins = Math.max(300, h * 60 + m - maxLate);
    return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  };

  const applyEarlyStart = async () => {
    try {
      const { data } = await api.post(`/tours/${t.id}/apply-early-start`);
      setT(data); reload();
      const still = (data.stops || []).filter((s) => s.appointment_late);
      if (still.length === 0) toast.success(`Partenza anticipata alle ${data.start_time}: tutti gli appuntamenti rispettati`);
      else toast.warning(`Partenza anticipata alle ${data.start_time}: ${still.length} appuntamento/i ancora critico/i (limite 05:00)`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const move = (idx, dir) => {
    const stops = [...t.stops];
    const j = idx + dir;
    if (j < 0 || j >= stops.length) return;
    if (stops[idx].locked || stops[j].locked) { toast.error("Tappa bloccata: sbloccala per riordinare"); return; }
    [stops[idx], stops[j]] = [stops[j], stops[idx]];
    persist(stops);
  };
  const toggleLock = (idx) => { const stops = [...t.stops]; stops[idx] = { ...stops[idx], locked: !stops[idx].locked }; persist(stops); };
  const assign = (field, val) => persist(t.stops, { [field]: val });

  const publish = async () => {
    const lateStops = (t.stops || []).filter((s) => s.appointment_late);
    if (lateStops.length > 0 && !window.confirm(
      `Attenzione: ${lateStops.length} tappa/e con arrivo stimato OLTRE l'appuntamento tassativo. ` +
      `Vuoi pubblicare comunque? È consigliato riordinare le tappe o rivedere il giro.`)) return;
    try { await api.post(`/tours/${t.id}/publish`); toast.success("Giro pubblicato"); reload(); } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => { if (!window.confirm(`Eliminare il giro ${t.code}?`)) return; try { await api.delete(`/tours/${t.id}`); toast.success("Eliminato"); reload(); } catch (e) { toast.error(apiErr(e)); } };

  const markers = (t.stops || []).filter((s) => s.lat != null).map((s, i) => ({ lat: s.lat, lng: s.lng, label: i + 1, title: s.label, subtitle: s.address }));
  const route = (t.stops || []).filter((s) => s.lat != null);

  const printPDF = () => openPrintWindow(`Foglio viaggio — Giro ${t.code}`, tourBlockHtml(t));

  const attachments = (t.stops || []).flatMap((s) => (s.operations || []).flatMap((o) => (o.photos || []).map((ph) => ({ ...ph, code: o.code }))));

  const published = t.status === "pubblicato" || t.status === "in_corso";
  const lateStops = (t.stops || []).filter((s) => s.appointment_late);

  return (
    <div className="lm-card p-5" data-testid={`tour-${t.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-lm-brown text-lm-cream flex items-center justify-center"><RouteIcon className="w-5 h-5" /></div>
          <div>
            <div className="flex items-center gap-2"><span className="font-display text-xl text-lm-brown">{t.code}</span><StatusBadge status={t.status} testid={`tour-status-${t.code}`} /></div>
            <p className="text-xs text-lm-muted">{fmtDate(t.date)} · {t.stops?.length || 0} tappe · {t.total_km || 0} km · {Math.round((t.total_duration_min || 0) / 60 * 10) / 10} h{t.start_time ? ` · partenza ${t.start_time}` : ""}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={t.vehicle_id || "__none"} onValueChange={(v) => assign("vehicle_id", v === "__none" ? null : v)}>
            <SelectTrigger data-testid={`assign-vehicle-${t.code}`} className="bg-white w-40 h-9"><Truck className="w-4 h-4 mr-1 text-lm-sand" /><SelectValue placeholder="Mezzo" /></SelectTrigger>
            <SelectContent><SelectItem value="__none">Nessun mezzo</SelectItem>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.identifier} ({v.category})</SelectItem>)}</SelectContent>
          </Select>
          <Select value={t.driver_id || "__none"} onValueChange={(v) => assign("driver_id", v === "__none" ? null : v)}>
            <SelectTrigger data-testid={`assign-driver-${t.code}`} className="bg-white w-40 h-9"><User className="w-4 h-4 mr-1 text-lm-sand" /><SelectValue placeholder="Autista" /></SelectTrigger>
            <SelectContent><SelectItem value="__none">Nessun autista</SelectItem>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {t.criticita?.length > 0 && (
        <div className="mt-3 text-xs text-[#9A6B00] bg-[#FEF8EC] border border-[#F3D295] rounded-lg px-3 py-2">
          {t.criticita.map((c, i) => <p key={i} className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> {c}</p>)}
        </div>
      )}

      {lateStops.length > 0 && (
        <div className="mt-3 text-xs text-[#991B1B] bg-[#FDF2F2] border border-[#F8B4B4] rounded-lg px-3 py-2.5 font-semibold flex flex-col gap-2" data-testid={`appt-warning-${t.code}`}>
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> {lateStops.length} appuntamento/i tassativo/i non rispettato/i: verificare prima di pubblicare.
          </div>
          {!published && proposedEarly() && (
            <button data-testid={`apply-early-${t.code}`} onClick={applyEarlyStart}
              className="self-start inline-flex items-center gap-1.5 rounded-lg bg-lm-brown text-lm-cream px-3 py-1.5 text-xs font-semibold hover:bg-lm-brown/90 transition-colors">
              <Clock className="w-3.5 h-3.5" /> Anticipa partenza alle {proposedEarly()}
            </button>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="space-y-2">
          {(t.stops || []).map((s, i) => (
            <div key={s.id || i} className={`rounded-xl border p-3 ${s.locked ? "border-lm-gold bg-[#FDF7EA]" : "border-lm-sandsoft bg-white"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-lm-brown text-lm-cream text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <div className="min-w-0">
                    <p className="font-semibold text-lm-text text-sm truncate">{s.label}</p>
                    <p className="text-xs text-lm-muted flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.address || "—"}</p>
                    {(s.eta_arrive) && <p className="text-xs text-lm-terra mt-0.5"><Clock className="w-3 h-3 inline mr-1" />Arrivo stimato {s.eta_arrive} · ripartenza {s.eta_depart}</p>}
                    {s.appointment && (
                      <p data-testid={`appt-${i}`} className={`text-xs mt-0.5 flex items-center gap-1 ${s.appointment_late ? "text-[#991B1B] font-semibold" : "text-[#5B3E86]"}`}>
                        <CalendarClock className="w-3 h-3" /> Appuntamento tassativo {s.appointment}
                        {s.appointment_late ? ` · ⚠ ARRIVO IN RITARDO (+${s.appointment_late_min} min)` : " · rispettato"}
                      </p>
                    )}
                    <div className="mt-1.5 space-y-1">
                      {(s.operations || []).map((o) => (
                        <div key={o.id} className="text-xs text-lm-text2 bg-lm-sandsoft/40 rounded px-2 py-1">
                          <b className="font-mono text-lm-brown">{o.code}</b> · {OPERATION_TYPES[o.operation_type]} · {o.goods_description || "merce"} <span className="text-lm-muted">({o.requester_name || "—"})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button data-testid={`stop-up-${i}`} onClick={() => move(i, -1)} className="w-7 h-7 rounded border border-lm-sandsoft hover:bg-lm-sandsoft flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button data-testid={`stop-down-${i}`} onClick={() => move(i, 1)} className="w-7 h-7 rounded border border-lm-sandsoft hover:bg-lm-sandsoft flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button data-testid={`stop-lock-${i}`} onClick={() => toggleLock(i)} title="Blocca tappa" className={`w-7 h-7 rounded border flex items-center justify-center ${s.locked ? "bg-lm-gold text-white border-lm-gold" : "border-lm-sandsoft hover:bg-lm-sandsoft"}`}>{s.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div><MapView markers={markers} route={route} height={320} testid={`tour-map-${t.code}`} /></div>
      </div>

      {t.ai_summary && (
        <div className="mt-4 rounded-xl bg-[#F4ECE1] border border-lm-sand p-3" data-testid={`ai-note-${t.code}`}>
          <p className="text-xs font-bold uppercase tracking-wider text-lm-brown mb-1 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Nota di viaggio (AI · GPT-5.4 Mini)</p>
          <p className="text-sm text-lm-text2 whitespace-pre-line leading-relaxed">{t.ai_summary}</p>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="mt-4" data-testid={`gallery-${t.code}`}>
          <p className="text-xs font-bold uppercase tracking-wider text-lm-brown mb-2 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Allegati autista ({attachments.length})</p>
          <div className="flex flex-wrap gap-2">
            {attachments.map((ph, i) => {
              const href = ph.url ? `${BACKEND}${ph.url}` : ph.data;
              const isImg = (ph.content_type || "").startsWith("image/") || !ph.content_type;
              return (
                <a key={i} href={href} target="_blank" rel="noreferrer" title={`${ph.code} · ${ph.name || "allegato"}`}
                  className="block w-20 h-20 rounded-lg overflow-hidden border border-lm-sandsoft bg-lm-sandsoft/40 flex items-center justify-center">
                  {isImg
                    ? <img src={href} alt={ph.name || "foto"} className="w-full h-full object-cover" />
                    : <span className="text-[10px] text-lm-brown font-semibold px-1 text-center">PDF/DDT</span>}
                </a>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        {!published && <Button data-testid={`publish-${t.code}`} onClick={publish} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream"><Send className="w-4 h-4 mr-2" /> Pubblica giro</Button>}
        {published && <span className="text-sm text-[#166534] font-medium flex items-center gap-1.5"><Send className="w-4 h-4" /> Pubblicato — l'autista è stato notificato</span>}
        <Button variant="outline" onClick={genAI} disabled={aiLoading} data-testid={`ai-btn-${t.code}`} className="border-lm-sand text-lm-brown">
          {aiLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} {t.ai_summary ? "Rigenera nota AI" : "Nota AI autista"}
        </Button>
        <Button variant="outline" onClick={printPDF} data-testid={`print-${t.code}`} className="border-lm-sand text-lm-brown"><Printer className="w-4 h-4 mr-2" /> Stampa PDF</Button>
        <Button variant="outline" onClick={del} className="border-lm-sand text-[#991B1B]"><Trash2 className="w-4 h-4 mr-2" /> Elimina</Button>
      </div>
    </div>
  );
}
